/**
 * Verifica se o elemento é uma aresta (Edge).
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isEdge(element) {
  return !!element?.source && !!element?.target;
}

/**
 * Verifica se o elemento é um nó (Node).
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isNode(element) {
  return !isEdge(element);
}

/**
 * Verifica se o elemento é uma tarefa.
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isTask(element) {
  return element?.type === "task";
}

/**
 * Verifica se o elemento é um evento de temporizador.
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isTimerEvent(element) {
  return element?.type === "timerEvent";
}

/**
 * Verifica se o elemento é o início do fluxo.
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isEventStart(element) {
  return element?.type === "eventStart";
}

/**
 * Verifica se o elemento é o fim do fluxo.
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isEventEnd(element) {
  return element?.type === "eventEnd";
}

/**
 * Verifica se o elemento é um nó paralelo.
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isParallel(element) {
  return element?.type === "parallel";
}

/**
 * Verifica se o elemento é um nó condicional.
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isConditional(element) {
  return element?.type === "conditional";
}
/**
 * Verifica se o elemento é um nó de fim de fluxo.
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isEndNode(element) {
  return isEventEnd(element);
}

/**
 * Verifica se o elemento é um nó de ramificação (parallel ou conditional).
 * @param {Node|Edge} element
 * @returns {boolean}
 */
export function isBranchNode(element) {
  return isParallel(element) || isConditional(element);
}
/**
 * Ordena a lista de estatísticas pelo campo `x`.
 * @param {Array} stats
 */
export function sortStats(stats) {
  stats.sort((a, b) => Number(a.x) - Number(b.x));
}

export function addStat(stats, title, time, taskId) {
  stats.push({ taskTitle: title, time, taskId });
}

/**
 * Processa um item do tipo 'timerEvent' e adiciona a estatistica ao array de estatisticas.
 * @param {NodeElement} item - O item a ser processado.
 * @param {Array} stats - O array de estatisticas.
 * @param {number} branchTime - O tempo total do caminho cr tico at  momento.
 * @returns {number} O tempo total do caminho cr tico ap s o processamento do item.
 */
export function processTimerEvent(item, stats, branchTime) {
  sortStats(stats);
  const timerTime = item.data.expiration.number / 24;
  stats.push({
    taskTitle: "TEMPORIZADOR",
    time: timerTime,
    taskId: item.id,
  });
  return branchTime + timerTime;
}

/**
 * Processa um item do tipo 'task' e adiciona a estatistica ao array de estatisticas.
 * @param {NodeElement} item - O item a ser processado.
 * @param {Array} stats - O array de estatisticas.
 * @param {number} branchTime - O tempo total do caminho cr tico at  momento.
 * @returns {number} O tempo total do caminho cr tico ap s o processamento do item.
 */
export function processTask(item, stats, branchTime) {
  sortStats(stats);
  const time = calculateTaskTime(item.data);
  // Calcula o peso (em dias de atraso) apenas para tarefas em andamento e atrasadas
  const weight = calculateTaskDelayWeight(item.data);
  stats.push({
    taskTitle: item.data.label,
    time,
    taskId: item.id,
    status: item.data.status,
    weight,
  });
  return branchTime + time;
}

/**
 * Calcula o tempo de uma task considerando status, datas e expiração.
 * O tempo retornado já está em dias, pronto para ser usado no stats e branchTime.
 * @param {NodeData} data - Dados do nó (task)
 * @returns {number} - Tempo calculado (em dias)
 */
export function calculateTaskTime(data) {
  const { status, startedAt, finishedAt, expiration } = data;
  const expirationNumber = expiration?.number || 0;
  const expirationMs = expirationNumber * 60 * 60 * 1000; // horas para ms
  const now = Date.now();

  if (status === "done" && startedAt && finishedAt) {
    return (finishedAt - startedAt) / (1000 * 60 * 60 * 24);
  } else if (status === "doing" && startedAt) {
    const expectedEnd = startedAt + expirationMs;
    if (now > expectedEnd) {
      const planned = expirationNumber / 24;
      const delay = (now - expectedEnd) / (1000 * 60 * 60 * 24);
      return planned + delay;
    } else {
      return expirationNumber / 24;
    }
  } else if (status === "pending") {
    return expirationNumber / 24;
  } else {
    return expirationNumber / 24;
  }
}

/**
 * Calcula o peso de criticidade de uma task em dias de atraso.
 * Apenas tarefas em andamento (doing) e já atrasadas contribuem para o peso.
 * Tarefas concluídas (done), pendentes (pending) ou no prazo não impactam o peso.
 * @param {NodeData} data
 * @returns {number} dias de atraso (>= 0)
 */
export function calculateTaskDelayWeight(data) {
  const { status, startedAt, expiration } = data || {};
  const expirationNumber = expiration?.number || 0;
  if (status !== "doing" || !startedAt || !expirationNumber) return 0;

  const expectedEnd = startedAt + expirationNumber * 60 * 60 * 1000; // horas -> ms
  const now = Date.now();
  if (now <= expectedEnd) return 0; // ainda no prazo

  const delayDays = (now - expectedEnd) / (1000 * 60 * 60 * 24);
  return delayDays > 0 ? delayDays : 0;
}
